import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useDashboardStats } from '../hooks/useDashboard';
import { useTrafficLogs } from '../hooks/useTrafficLogs';

const DashboardPage = () => {
  const navigate = useNavigate();
  const { profile, signOut } = useAuth();
  const { stats, loading: statsLoading } = useDashboardStats();
  const { chartData, selectedDate, changeDate, getAvailableDates, loading: logsLoading } = useTrafficLogs();

  const [isSigningOut, setIsSigningOut] = useState(false);

  const handleSignOut = async () => {
    setIsSigningOut(true);
    await signOut();
    navigate('/login');
  };

  const availableDates = getAvailableDates();
  const totalDataUsed = stats.dataUsed.residentialBasic +
    stats.dataUsed.residentialPremium +
    stats.dataUsed.datacenter +
    stats.dataUsed.isp;

  const isAccountActive = !!profile || stats.activeProxies > 0 || totalDataUsed > 0;
  const accountStatusLabel = isAccountActive ? 'Active' : 'Inactive';
  const accountStatusColor = isAccountActive ? '#22c55e' : '#ef4444';

  const hasTrafficData = chartData.some(d => d.value > 0);

  return (
    <div className="dashboard-page">
      <h1 className="page-title">Dashboard</h1>

      {/* Welcome Card */}
      <div className="welcome-card">
        <div className="welcome-left">
          <div className="user-avatar">
            <span>{profile?.name?.charAt(0)?.toUpperCase() || profile?.username?.charAt(0)?.toUpperCase() || 'U'}</span>
          </div>
          <div className="user-info">
            <h2>Welcome</h2>
            <p className="user-name">{profile?.name || profile?.username || 'User'}</p>
            <p className="user-username">Proxy Username: {profile?.username || 'Loading...'}</p>
          </div>
        </div>
        <button
          className="sign-out-btn"
          onClick={handleSignOut}
          disabled={isSigningOut}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
            <polyline points="16,17 21,12 16,7"/>
            <line x1="21" y1="12" x2="9" y2="12"/>
          </svg>
          {isSigningOut ? 'Signing out...' : 'Sign out'}
        </button>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <span className="stat-label">Active Proxies</span>
          <h3 className="stat-value">
            {statsLoading ? '...' : stats.activeProxies}
          </h3>
          <span className="stat-sublabel">Currently active connections</span>
        </div>
        <div className="stat-card accent">
          <span className="stat-label">Total Data Used</span>
          <h3 className="stat-value">
            {statsLoading ? '...' : `${totalDataUsed.toFixed(2)} GB`}
          </h3>
          <span className="stat-sublabel">Across all proxy types</span>
        </div>
        <div className="stat-card accent">
          <span className="stat-label">Account Status</span>
          <h3 className="stat-value" style={{ color: accountStatusColor }}>
            {statsLoading ? '...' : accountStatusLabel}
          </h3>
          <span className="stat-sublabel">
            {isAccountActive ? 'Your account is in good standing' : 'No active packages or proxies'}
          </span>
        </div>
      </div>

      {/* Proxies and Data */}
      <div className="proxies-data-grid">
        <div className="info-card">
          <span className="info-label">Current active proxies</span>
          <p className="info-value">
            Total: {statsLoading ? '...' : stats.activeProxies} active proxies
          </p>
        </div>
        <div className="info-card">
          <span className="info-label">Total data used</span>
          <div className="data-list">
            <p><strong>Rotating Residential Basic:</strong> {stats.dataUsed.residentialBasic.toFixed(2)} GB</p>
            <p><strong>Rotating Residential Premium:</strong> {stats.dataUsed.residentialPremium.toFixed(2)} GB</p>
            <p><strong>Rotating ISP:</strong> {stats.dataUsed.isp.toFixed(2)} GB</p>
            <p><strong>Rotating Datacenter:</strong> {stats.dataUsed.datacenter.toFixed(2)} GB</p>
          </div>
          <p className="data-total">Total: {totalDataUsed.toFixed(2)} GB used</p>
        </div>
      </div>

      {/* Traffic Logs */}
      <div className="traffic-logs-section">
        <div className="traffic-header">
          <div>
            <h3>Rotating Traffic Logs</h3>
            <p>
              {logsLoading
                ? 'Loading...'
                : hasTrafficData
                  ? `Traffic data for ${selectedDate}`
                  : 'You have not used any data in this period.'
              }
            </p>
          </div>
          <div className="date-selector">
            <select value={selectedDate} onChange={(e) => changeDate(e.target.value)}>
              {availableDates.map(date => (
                <option key={date} value={date}>{date}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="traffic-chart">
          <div className="chart-container">
            <div className="chart-y-axis">
              <span>1.0</span>
              <span>0.9</span>
              <span>0.8</span>
              <span>0.7</span>
              <span>0.6</span>
              <span>0.5</span>
              <span>0.4</span>
              <span>0.3</span>
              <span>0.2</span>
              <span>0.1</span>
              <span>0</span>
            </div>
            <div className="chart-area">
              {!hasTrafficData && <div className="no-data-message">No data</div>}
              <div className="chart-grid">
                {[...Array(10)].map((_, i) => (
                  <div key={i} className="grid-line"></div>
                ))}
              </div>
              {hasTrafficData && (
                <div className="chart-bars">
                  {chartData.map((d, i) => (
                    <div
                      key={i}
                      className="chart-bar"
                      style={{ height: `${Math.min(d.value * 100, 100)}%` }}
                      title={`${d.label}: ${d.value} GB`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="chart-x-axis">
            <span>0</span>
            <span>0.1</span>
            <span>0.2</span>
            <span>0.3</span>
            <span>0.4</span>
            <span>0.5</span>
            <span>0.6</span>
            <span>0.7</span>
            <span>0.8</span>
            <span>0.9</span>
            <span>1.0</span>
          </div>
          <div className="chart-label">GB Used</div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
